import React, { useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Upload, Download, FileSpreadsheet, Loader2, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const TEMPLATE_HEADER = "somcore_ref,date,client,client_ref,title,industry,outcome,value,currency,cost,client_po_ref,client_po_date,notes";
const TEMPLATE_ROWS = [
  ",2025-03-14,Kalgoorlie Gold Mines,RFQ-2025-014,Lifting & rigging package,mining,won,125000,USD,98000,PO-55231,2025-03-20,Delivered Q2",
  ",2025-05-02,North Sea Petroleum,ENQ-8821,Sealing kits,oil_and_gas,lost,42000,USD,,,,Lost on price",
  ",2025-06-11,Gulf Offshore Energy,,Instrumentation spares,oil_and_gas,quoted,18750,USD,,,,Awaiting decision",
];
const ACTION_LABEL = { new: "New", update: "Update", same: "No Change" };
const ACTION_CLS = { new: "bg-emerald-100 text-emerald-700", update: "bg-amber-100 text-amber-800", same: "bg-slate-100 text-slate-500" };
const FIELD_LABEL = { date: "Date", client: "Client", client_ref: "Client Ref", title: "Title", outcome: "Outcome", value: "Value", currency: "Currency", cost: "Cost", client_po_ref: "Client PO", client_po_date: "PO Date", owner: "Owner", notes: "Notes" };

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const STAGE_LABEL = { won: "Won", lost: "Lost", quoted: "Quoted", new: "Open" };
const STAGE_CLS = {
  won: "bg-amber-100 text-amber-800", lost: "bg-rose-100 text-rose-700",
  quoted: "bg-sky-100 text-sky-700", new: "bg-slate-100 text-slate-600",
};

export default function ImportHistoryDialog({ open, onOpenChange, onDone }) {
  const [rows, setRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [exporting, setExporting] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setRows([]); setSummary(null); setParsing(false); setCommitting(false); };
  const close = () => { reset(); onOpenChange(false); };

  const downloadRegister = async () => {
    setExporting(true);
    try {
      const res = await api.get("/rfqs/import/history/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `Somcore-RFQ-Register-${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err) { toast.error(apiError(err)); } finally { setExporting(false); }
  };

  const downloadTemplate = () => {
    const csv = [TEMPLATE_HEADER, ...TEMPLATE_ROWS].join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "somcore_rfq_history_template.csv"; a.click();
    setTimeout(() => window.URL.revokeObjectURL(url), 30000);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    const source_type = name.endsWith(".csv") ? "csv" : "excel";
    setParsing(true);
    try {
      const file_base64 = await readBase64(file);
      const { data } = await api.post("/rfqs/import/history/parse", { source_type, file_base64, filename: file.name });
      const order = { new: 0, update: 1, same: 2 };
      setRows([...(data.rows || [])].sort((a, b) => (order[a.action] ?? 0) - (order[b.action] ?? 0)));
      setSummary(data.summary || null);
      toast.success(`Found ${data.count} Row(s): ${data.summary?.new || 0} New, ${data.summary?.update || 0} To Update, ${data.summary?.same || 0} Unchanged.`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setParsing(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeRow = (idx) => setRows((rs) => rs.filter((_, i) => i !== idx));

  const commit = async () => {
    if (!rows.length) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/rfqs/import/history/commit", { rows });
      const bits = [`${data.created} New RFQ(s)`, `${data.updated || 0} Updated`];
      if (data.unchanged) bits.push(`${data.unchanged} Unchanged`);
      if (data.clients_created) bits.push(`${data.clients_created} New Client(s)`);
      if (data.orders_created) bits.push(`${data.orders_created} Order(s)`);
      toast.success(`Done: ${bits.join(", ")}.`);
      if (data.errors?.length) toast.error(`${data.errors.length} row(s) skipped — check dates & values.`);
      close(); onDone?.();
    } catch (err) { toast.error(apiError(err)); }
    finally { setCommitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : close())}>
      <DialogContent className="max-w-4xl" data-testid="import-history-dialog">
        <DialogHeader>
          <DialogTitle>Import Past RFQs & Orders</DialogTitle>
          <DialogDescription>
            Download The Current RFQ Register, Edit It Or Add New Rows, Then Upload It Back. Existing RFQs Are Matched By Somcore Ref And Updated — Never Duplicated. Rows Without A Somcore Ref Are Added As New.
          </DialogDescription>
        </DialogHeader>

        {!rows.length ? (
          <div className="space-y-4 py-2">
            <div className="rounded-lg border border-dashed border-border bg-slate-50 p-4 text-sm text-slate-600">
              <p className="font-medium text-slate-800">How It Works</p>
              <ol className="mt-1 list-decimal space-y-0.5 pl-5">
                <li>Download The Current RFQ Register (Excel) — Every RFQ With Its Somcore Ref, Status And Details.</li>
                <li>Change Any Status Or Info In Place, And Add New RFQs As New Rows With The Somcore Ref Left Blank.</li>
                <li>Upload It. You'll See What's New, What Will Be Updated And What's Unchanged Before Anything Is Saved. Blank Cells Never Erase Existing Data.</li>
              </ol>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button onClick={downloadRegister} disabled={exporting} data-testid="download-register-btn" className="bg-[#1B4383] hover:bg-[#16386e]">
                {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />}Download Current RFQs (Excel)
              </Button>
              <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
                <Download className="mr-2 h-4 w-4" />Blank Template (CSV)
              </Button>
              <Button onClick={() => fileRef.current?.click()} disabled={parsing} data-testid="upload-history-btn">
                {parsing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                {parsing ? "Reading..." : "Upload Filled Sheet (CSV or Excel)"}
              </Button>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="history-file-input" />
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700" data-testid="import-summary">{rows.length} Row(s) · {summary?.new || 0} New · {summary?.update || 0} To Update · {summary?.same || 0} Unchanged</span>
              <Button variant="ghost" size="sm" onClick={reset} data-testid="import-reset-btn">Choose a different file</Button>
            </div>
            <div className="max-h-[52vh] overflow-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Action</th>
                    <th className="px-3 py-2 font-medium">Somcore Ref</th>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Client</th>
                    <th className="px-3 py-2 font-medium">Ref</th>
                    <th className="px-3 py-2 font-medium">Title</th>
                    <th className="px-3 py-2 font-medium">Outcome</th>
                    <th className="px-3 py-2 text-right font-medium">Value</th>
                    <th className="px-3 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-border" data-testid={`import-row-${i}`}>
                      <td className="px-3 py-1.5">
                        <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${ACTION_CLS[r.action] || ACTION_CLS.new}`} data-testid={`import-action-${i}`}>{ACTION_LABEL[r.action] || "New"}</span>
                        {r.changes?.length > 0 && <div className="mt-0.5 max-w-[160px] text-[10px] text-amber-700">{r.changes.map((c) => FIELD_LABEL[c] || c).join(", ")}</div>}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-xs">{r.somcore_ref || "—"}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{r.date || "—"}</td>
                      <td className="px-3 py-1.5">{r.client || "—"}</td>
                      <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{r.client_ref || "—"}</td>
                      <td className="max-w-[220px] truncate px-3 py-1.5">{r.title || "—"}</td>
                      <td className="px-3 py-1.5">
                        <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${STAGE_CLS[r.outcome] || STAGE_CLS.new}`}>
                          {STAGE_LABEL[r.outcome] || "Open"}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-xs">{r.value ? `${(r.currency || "TZS").toUpperCase()} ${Number(String(r.value).replace(/,/g, "")).toLocaleString()}` : "—"}</td>
                      <td className="px-3 py-1.5 text-right">
                        <button onClick={() => removeRow(i)} className="text-slate-400 hover:text-destructive" data-testid={`import-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          {rows.length > 0 && (
            <Button onClick={commit} disabled={committing || !rows.some((r) => r.action !== "same")} data-testid="commit-history-btn">
              {committing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              Save {rows.filter((r) => r.action !== "same").length} Change{rows.filter((r) => r.action !== "same").length !== 1 ? "s" : ""}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
